import {DndContext, DragOverlay} from '@dnd-kit/core';

import type {ReactNode} from 'react';

import type {DragOverlayModel} from '../../hooks/useDragOverlay';
import type {useGridDragAndDrop} from '../../hooks/useGridDragAndDrop';
import {DragOverlayColumn} from '../DragOverlayColumn/DragOverlayColumn';

type ConditionalDndWrapperProps = {
  columnMovingAllowed: boolean;
  dnd: ReturnType<typeof useGridDragAndDrop>;
  dragOverlay: DragOverlayModel | null;
  children: ReactNode;
};

/**
 * DndContext must wrap a <div>, never a <table> directly. dnd-kit injects
 * accessibility <div>s as DndContext children; placing them inside <table> is
 * invalid HTML and triggers hydration errors. When columnMovingAllowed is
 * false, render children without the DndContext wrapper (useSortable in
 * HeaderCell and useDraggable/useDroppable in GroupHeaderCell tolerate a
 * missing context).
 */
export function ConditionalDndWrapper(props: ConditionalDndWrapperProps) {
  if (!props.columnMovingAllowed) {
    return <>{props.children}</>;
  }

  return (
    <DndContext
      sensors={props.dnd.sensors}
      collisionDetection={props.dnd.collisionDetection}
      onDragStart={props.dnd.handleDragStart}
      onDragOver={props.dnd.handleDragOver}
      onDragEnd={props.dnd.handleDragEnd}
      onDragCancel={props.dnd.handleDragCancel}
    >
      {props.children}
      <DragOverlay>
        {/* eslint-disable-next-line eag/prefer-show -- Pre-existing violation */}
        {props.dragOverlay ? (
          <DragOverlayColumn
            headerName={props.dragOverlay.headerName}
            width={props.dragOverlay.width}
          />
        ) : null}
      </DragOverlay>
    </DndContext>
  );
}
